import { App, inject, InjectionKey } from 'vue';
import sanityClient from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import { SanityImageObject } from '@sanity/image-url/lib/types/types';
import { BasicRouterLink, Sanity, TransformedImage } from '@/types';
export const sanityKey: InjectionKey<Sanity> = Symbol('sanity');

function createSanity() {
  const useCdn = process.env.NODE_ENV === 'production' ? true : false;
  const client = sanityClient({
    projectId: 'l9i9mp7f',
    dataset: 'production',
    useCdn,
  });

  const builder = imageUrlBuilder(client);

  const sanity = {
    client,
    builder,
    install(app: App) {
      app.provide(sanityKey, sanity);
    },
  };

  return sanity;
}

const sanity = createSanity();

export default sanity;

export function useSanity(): Sanity {
  return inject(sanityKey)!;
}

export function dimensionsFromRef(ref: string) {
  const dimStr = ref.split('-')[2] || '';
  const [width, height] = dimStr.split('x').map((dim) => parseInt(dim, 10));
  return {
    width,
    height,
    ratio: width / height,
  };
}

export function slugToLink(
  name: string,
  slug: string
): BasicRouterLink<typeof name> {
  const params = { [name]: slug };
  return { name, params };
}

export function getImageAttributes(
  image: SanityImageObject,
  width = 1000,
  widths?: Array<number>
): TransformedImage {
  const img = sanity.builder.image(image);
  const src = img.width(width).url()!;
  const srcset = widths
    ? widths.map((width) => `${img.width(width).url()!} ${width}w`).join(', ')
    : '';
  return {
    src,
    srcset,
    ...dimensionsFromRef(image.asset._ref || ''),
  };
}

export function getImageAttributesRatio(
  image: SanityImageObject,
  width: number,
  ratio: number
): TransformedImage {
  const img = sanity.builder.image(image);
  const height = width / ratio;
  const src = img.width(width).height(Math.round(height)).url()!;
  return {
    src,
    width,
    height,
    ratio,
  };
}
